import { useCallback, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import { Box, Checkbox, Collapse, IconButton, Typography, useTheme } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ServiceHierarchyNode } from "../Domain/serviceNotificationHierarchy.types";
import {
  applyLeavesUnder,
  isEveryLeafEnabled,
  isLeafNode,
  isSomeLeafEnabled,
  nextAggregateChecked
} from "../functions/serviceNotificationHierarchy.helpers";
import { resolveNodeLabel } from "../functions/resolveNodeLabel";

type NodeLabelTextProps = {
  primaryLabel: string;
  subtitleLiteral?: string;
  fontWeight?: number;
};

export function NodeLabelText({
  primaryLabel,
  subtitleLiteral,
  fontWeight
}: Readonly<NodeLabelTextProps>) {
  const resolvedFontWeight = fontWeight ?? 400;
  const sub = subtitleLiteral?.trim();
  if (sub) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          variant="subtitle2"
          sx={{ lineHeight: 1.4, fontWeight: resolvedFontWeight }}
        >
          {primaryLabel}
        </Typography>
        <Typography
          component="div"
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.35 }}
        >
          {sub}
        </Typography>
      </Box>
    );
  }
  return (
    <Typography
      component="div"
      variant="subtitle2"
      sx={{ lineHeight: 1.4, fontWeight: resolvedFontWeight, flex: 1, minWidth: 0 }}
    >
      {primaryLabel}
    </Typography>
  );
}

export type ApprovalLocationHierarchyRowProps = {
  node: ServiceHierarchyNode;
  /** Nesting level from the tree root (0 = top); drives left padding so child rows indent. */
  depth: number;
  enabled: Record<string, boolean>;
  setEnabled: Dispatch<SetStateAction<Record<string, boolean>>>;
  expanded: Record<string, boolean>;
  toggleExpanded: (id: string) => void;
};

export function ApprovalLocationHierarchyRow({
  node,
  depth,
  enabled,
  setEnabled,
  expanded,
  toggleExpanded
}: Readonly<ApprovalLocationHierarchyRowProps>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const label = resolveNodeLabel(node, t);
  /** Extra indent per depth so nested rows read clearly as a tree (8px grid units). */
  const paddingLeft = theme.spacing(2 + depth * 3);
  const isOpen = expanded[node.id] === true;
  const hasChildren = !isLeafNode(node);

  const handleAggregateChange = useCallback(() => {
    setEnabled(prev => {
      const turnOn = nextAggregateChecked(node, prev);
      return applyLeavesUnder(node, turnOn, prev);
    });
  }, [node, setEnabled]);

  const handleLeafChange = useCallback(() => {
    setEnabled(prev => ({ ...prev, [node.id]: !prev[node.id] }));
  }, [node.id, setEnabled]);

  const handleAggregateRowClick = useCallback(() => {
    toggleExpanded(node.id);
  }, [node.id, toggleExpanded]);

  const handleCheckboxClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  if (!hasChildren) {
    return (
      <Box
        data-testid={`approval-location-hierarchy-row-${node.id}`}
        sx={{
          pl: paddingLeft,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          minHeight: 48
        }}
      >
        <Checkbox
          data-testid={`approval-location-hierarchy-leaf-${node.id}`}
          checked={enabled[node.id] === true}
          onChange={handleLeafChange}
          sx={{ mt: 0.25 }}
        />
        <Box sx={{ ml: 0.5, flex: 1, minWidth: 0 }}>
          <NodeLabelText
            primaryLabel={label}
            subtitleLiteral={node.subtitleLiteral}
            fontWeight={400}
          />
        </Box>
      </Box>
    );
  }

  const allOn = isEveryLeafEnabled(node, enabled);
  const someOn = isSomeLeafEnabled(node, enabled);
  const indeterminate = !allOn && someOn;

  return (
    <Box data-testid={`approval-location-hierarchy-row-${node.id}`}>
      <Box
        onClick={handleAggregateRowClick}
        sx={{
          pl: paddingLeft,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          minHeight: 48,
          cursor: "pointer"
        }}
      >
        <Checkbox
          data-testid={`approval-location-hierarchy-aggregate-${node.id}`}
          checked={allOn}
          indeterminate={indeterminate}
          onChange={handleAggregateChange}
          onClick={handleCheckboxClick}
          sx={{ mt: 0.25 }}
        />
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-label={
            isOpen
              ? t("_serviceNotificationHierarchy_collapse")
              : t("_serviceNotificationHierarchy_expand")
          }
          sx={{
            ml: 0.5,
            flex: 1,
            display: "flex",
            alignItems: "center",
            minHeight: 48
          }}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleAggregateRowClick();
            }
          }}
        >
          <NodeLabelText
            primaryLabel={label}
            subtitleLiteral={node.subtitleLiteral}
            fontWeight={allOn ? 600 : 400}
          />
          <IconButton
            data-testid={`approval-location-hierarchy-expand-${node.id}`}
            tabIndex={-1}
            aria-hidden={true}
            sx={{
              alignSelf: "flex-start",
              mt: 0.25,
              transition: "transform 0.3s",
              color: theme.palette.mode === "light" ? "black" : "white",
              pointerEvents: "none",
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)"
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {node.children?.map(child => (
          <ApprovalLocationHierarchyRow
            key={child.id}
            node={child}
            depth={depth + 1}
            enabled={enabled}
            setEnabled={setEnabled}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </Collapse>
    </Box>
  );
}
